import React, { useEffect, useMemo, useState } from "react";

import {
  getAdminOrders,
  updateAdminOrderStatus,
} from "../api/adminOrderService";

import "./AdminOrders.css";

const statuses = [
  "PLACED",
  "CONFIRMED",
  "PACKED",
  "SHIPPED",
  "DELIVERED",
  "CANCELLED",
];

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [searchText, setSearchText] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("ALL");

  const [updatingOrderId, setUpdatingOrderId] = useState(null);

  const loadOrders = async () => {
    try {
      setLoading(true);
      setError("");

      const data = await getAdminOrders();

      setOrders(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Unable to load orders:", error);
      setError("Unable to load customer orders.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOrders();
  }, []);

  const handleStatusChange = async (orderId, status) => {
    try {
      setUpdatingOrderId(orderId);

      const updatedOrder = await updateAdminOrderStatus(orderId, status);

      setOrders((currentOrders) =>
        currentOrders.map((order) =>
          order.id === updatedOrder.id ? updatedOrder : order,
        ),
      );
    } catch (error) {
      console.error("Status update failed:", error);
      alert("Unable to update order status.");
    } finally {
      setUpdatingOrderId(null);
    }
  };

  const filteredOrders = useMemo(() => {
    return orders.filter((order) => {
      const search = searchText.trim().toLowerCase();

      const matchesSearch =
        search === "" ||
        String(order.id).toLowerCase().includes(search) ||
        (order.customerName || "").toLowerCase().includes(search) ||
        (order.phone || "").toLowerCase().includes(search) ||
        (order.email || "").toLowerCase().includes(search);

      const matchesStatus =
        selectedStatus === "ALL" || order.status === selectedStatus;

      return matchesSearch && matchesStatus;
    });
  }, [orders, searchText, selectedStatus]);

  const totalOrders = orders.length;

  const placedOrders = orders.filter(
    (order) => order.status === "PLACED",
  ).length;

  const processingOrders = orders.filter(
    (order) =>
      order.status === "CONFIRMED" ||
      order.status === "PACKED" ||
      order.status === "SHIPPED",
  ).length;

  const deliveredOrders = orders.filter(
    (order) => order.status === "DELIVERED",
  ).length;

  const cancelledOrders = orders.filter(
    (order) => order.status === "CANCELLED",
  ).length;

  const formatPrice = (amount) => {
    if (amount === null || amount === undefined) {
      return "₹0";
    }

    return `₹${Number(amount).toLocaleString("en-IN")}`;
  };

  const formatDate = (date) => {
    if (!date) {
      return "-";
    }

    return new Date(date).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatTime = (date) => {
    if (!date) {
      return "";
    }

    return new Date(date).toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <div className="admin-orders-message">
        <div className="admin-orders-loader"></div>
        <p>Loading customer orders...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-orders-message error">
        <div className="admin-orders-error-icon">!</div>
        <h2>Something went wrong</h2>
        <p>{error}</p>

        <button
          type="button"
          className="admin-orders-retry-button"
          onClick={loadOrders}
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="admin-orders-page">
      {/* PAGE HEADER */}
      <div className="admin-orders-header">
        <div className="admin-orders-heading">
          <p className="admin-orders-eyebrow">STAR GOLD / SALES</p>

          <h1>Customer Orders</h1>

          <span>Review customer purchases and manage order progress.</span>
        </div>

        <button
          type="button"
          className="admin-refresh-button"
          onClick={loadOrders}
          title="Refresh orders"
        >
          <span>↻</span>
          Refresh
        </button>
      </div>

      {/* SUMMARY CARDS */}
      <div className="admin-order-summary">
        <div className="admin-order-summary-card">
          <div className="summary-icon">◇</div>

          <div>
            <span>Total Orders</span>
            <strong>{totalOrders}</strong>
          </div>
        </div>

        <div className="admin-order-summary-card">
          <div className="summary-icon">◈</div>

          <div>
            <span>New Orders</span>
            <strong>{placedOrders}</strong>
          </div>
        </div>

        <div className="admin-order-summary-card">
          <div className="summary-icon">▱</div>

          <div>
            <span>Processing</span>
            <strong>{processingOrders}</strong>
          </div>
        </div>

        <div className="admin-order-summary-card">
          <div className="summary-icon">✓</div>

          <div>
            <span>Delivered</span>
            <strong>{deliveredOrders}</strong>
          </div>
        </div>

        <div className="admin-order-summary-card cancelled-card">
          <div className="summary-icon">×</div>

          <div>
            <span>Cancelled</span>
            <strong>{cancelledOrders}</strong>
          </div>
        </div>
      </div>

      {/* FILTER BAR */}
      <div className="admin-orders-toolbar">
        <div className="admin-order-search">
          <span>⌕</span>

          <input
            type="text"
            placeholder="Search by order ID, customer, phone or email..."
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
          />
        </div>

        <div className="admin-order-filter">
          <label>Status</label>

          <select
            value={selectedStatus}
            onChange={(event) => setSelectedStatus(event.target.value)}
          >
            <option value="ALL">All Orders</option>

            {statuses.map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* RESULTS INFO */}
      <div className="admin-orders-results-info">
        <span>
          Showing <strong>{filteredOrders.length}</strong> of{" "}
          <strong>{orders.length}</strong> orders
        </span>

        {(searchText || selectedStatus !== "ALL") && (
          <button
            type="button"
            onClick={() => {
              setSearchText("");
              setSelectedStatus("ALL");
            }}
          >
            Clear filters
          </button>
        )}
      </div>

      {/* EMPTY STATE */}
      {orders.length === 0 ? (
        <div className="admin-orders-empty">
          <div className="admin-empty-icon">◇</div>

          <h2>No Orders Yet</h2>

          <p>
            Customer orders will appear here once customers complete their
            purchases.
          </p>
        </div>
      ) : filteredOrders.length === 0 ? (
        <div className="admin-orders-empty">
          <div className="admin-empty-icon">⌕</div>

          <h2>No Matching Orders</h2>

          <p>Try changing your search or status filter.</p>

          <button
            type="button"
            onClick={() => {
              setSearchText("");
              setSelectedStatus("ALL");
            }}
          >
            Clear Filters
          </button>
        </div>
      ) : (
        /* ORDERS TABLE */
        <div className="admin-orders-table-wrapper">
          <table className="admin-orders-table">
            <thead>
              <tr>
                <th>Order</th>
                <th>Customer</th>
                <th>Products</th>
                <th>Amount</th>
                <th>Payment</th>
                <th>Date</th>
                <th>Status</th>
              </tr>
            </thead>

            <tbody>
              {filteredOrders.map((order) => (
                <tr key={order.id}>
                  {/* ORDER */}
                  <td>
                    <div className="admin-order-id">
                      <strong>#{order.id}</strong>

                      <span>
                        {order.items?.length || 0} item
                        {(order.items?.length || 0) !== 1 ? "s" : ""}
                      </span>
                    </div>
                  </td>

                  {/* CUSTOMER */}
                  <td>
                    <div className="admin-customer">
                      <strong>{order.customerName || "Customer"}</strong>

                      {order.phone && <span>{order.phone}</span>}

                      {order.email && <span>{order.email}</span>}

                      {order.city && (
                        <small>
                          {order.city}
                          {order.state ? `, ${order.state}` : ""}
                        </small>
                      )}
                    </div>
                  </td>

                  {/* PRODUCTS */}
                  <td>
                    <div className="admin-order-products">
                      {order.items?.length > 0 ? (
                        order.items.map((item, index) => (
                          <div
                            key={item.id || `${order.id}-${index}`}
                            className="admin-order-product"
                          >
                            <div>
                              <span>{item.productName}</span>

                              <small>
                                ₹
                                {Number(item.price || 0).toLocaleString(
                                  "en-IN",
                                )}
                              </small>
                            </div>

                            <strong>× {item.quantity}</strong>
                          </div>
                        ))
                      ) : (
                        <span className="no-products">No product details</span>
                      )}
                    </div>
                  </td>

                  {/* TOTAL */}
                  <td>
                    <strong className="admin-order-total">
                      {formatPrice(order.totalAmount)}
                    </strong>
                  </td>

                  {/* PAYMENT */}
                  <td>
                    <span
                      className={`admin-payment-status payment-${(
                        order.paymentStatus || "PENDING"
                      ).toLowerCase()}`}
                    >
                      {order.paymentStatus || "PENDING"}
                    </span>
                  </td>

                  {/* DATE */}
                  <td>
                    <div className="admin-order-date">
                      <strong>{formatDate(order.orderDate)}</strong>

                      <span>{formatTime(order.orderDate)}</span>
                    </div>
                  </td>

                  {/* STATUS */}
                  <td>
                    <select
                      className={`admin-order-status status-${(
                        order.status || ""
                      ).toLowerCase()}`}
                      value={order.status || "PLACED"}
                      disabled={updatingOrderId === order.id}
                      onChange={(event) =>
                        handleStatusChange(order.id, event.target.value)
                      }
                    >
                      {statuses.map((status) => (
                        <option key={status} value={status}>
                          {status}
                        </option>
                      ))}
                    </select>

                    {updatingOrderId === order.id && (
                      <span className="status-saving">Saving...</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminOrders;
